/**
 * annotationsYMap.js
 *
 * ydoc CRUD for annotation canvas data stored in Y.Map("annotations").
 * Each figure's annotations are keyed by the figure's blockId.
 *
 * Structure inside the Y.Doc:
 *
 *   Y.Map("annotations")
 *     └── [blockId]: Y.Map
 *           └── canvas: string  ← JSON.stringify(fabricCanvas.toJSON([custom props]))
 *
 * Comments live exclusively in the margin rail via the existing yComments mechanism.
 * This map owns only the Fabric.js canvas state.
 */

import * as Y from 'yjs';

// ---------------------------------------------------------------------------
// Entry helpers
// ---------------------------------------------------------------------------

/**
 * Get or create the Y.Map entry for a given blockId.
 * Must be called inside a ydoc.transact() block when creating.
 *
 * @param {import('yjs').Map} yAnnotations  Root Y.Map("annotations")
 * @param {string}            blockId
 * @returns {import('yjs').Map}
 */
export function getAnnotationEntry(yAnnotations, blockId) {
    if (!yAnnotations || !blockId) return null;
    if (!yAnnotations.has(blockId)) {
        const entry = new Y.Map();
        entry.set('canvas', '');
        yAnnotations.set(blockId, entry);
    }
    return yAnnotations.get(blockId);
}

// ---------------------------------------------------------------------------
// Write API
// ---------------------------------------------------------------------------

/**
 * Persist the Fabric.js canvas JSON string for a given blockId.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  yAnnotations
 * @param {string}             blockId
 * @param {string}             jsonStr  JSON.stringify(canvas.toJSON([...]))
 */
export function setAnnotationCanvas(ydoc, yAnnotations, blockId, jsonStr) {
    if (!ydoc || !yAnnotations || !blockId) return;
    ydoc.transact(() => {
        const entry = getAnnotationEntry(yAnnotations, blockId);
        if (entry) entry.set('canvas', jsonStr ?? '');
    }, 'annotation-editor');
}

/**
 * Persist the merged image URL for a given blockId.
 * Previously stored a PNG data URL; now stores the uploaded image URL from S3/MinIO.
 * Falls back gracefully if a data URL is provided (e.g. when offline).
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  yAnnotations
 * @param {string}             blockId
 * @param {string}             urlOrDataURL  Uploaded image URL (preferred) or PNG data URL (fallback)
 */
export function setAnnotationMerged(ydoc, yAnnotations, blockId, urlOrDataURL) {
    if (!ydoc || !yAnnotations || !blockId) return;
    ydoc.transact(() => {
        const entry = getAnnotationEntry(yAnnotations, blockId);
        if (entry) entry.set('merged', urlOrDataURL ?? '');
    }, 'annotation-editor');
}

// ---------------------------------------------------------------------------
// Read API
// ---------------------------------------------------------------------------

/**
 * Read the current canvas JSON string for a blockId.
 * Returns an empty string if no entry exists yet.
 *
 * @param {import('yjs').Map} yAnnotations
 * @param {string}            blockId
 * @returns {string}
 */
export function getAnnotationCanvas(yAnnotations, blockId) {
    if (!yAnnotations || !blockId) return '';
    const entry = yAnnotations.get(blockId);
    return entry ? (entry.get('canvas') || '') : '';
}

/**
 * Read the merged image URL (or fallback data URL) for a blockId.
 * Returns an empty string if no merged image has been stored yet.
 *
 * @param {import('yjs').Map} yAnnotations
 * @param {string}            blockId
 * @returns {string}
 */
export function getAnnotationMerged(yAnnotations, blockId) {
    if (!yAnnotations || !blockId) return '';
    const entry = yAnnotations.get(blockId);
    return entry ? (entry.get('merged') || '') : '';
}

/**
 * Returns true if a blockId has saved annotation data.
 *
 * @param {import('yjs').Map} yAnnotations
 * @param {string}            blockId
 * @returns {boolean}
 */
export function hasAnnotation(yAnnotations, blockId) {
    if (!yAnnotations || !blockId) return false;
    const entry = yAnnotations.get(blockId);
    if (!entry) return false;
    const canvas = entry.get('canvas');
    return typeof canvas === 'string' && canvas.length > 0;
}

// ---------------------------------------------------------------------------
// Observation
// ---------------------------------------------------------------------------

/**
 * Subscribe to canvas changes for a specific blockId.
 * Fires whenever the 'canvas' key is updated for this figure.
 *
 * @param {import('yjs').Map} yAnnotations
 * @param {string}            blockId
 * @param {Function}          callback  () => void
 * @returns {Function}  unsubscribe function
 */
export function observeAnnotation(yAnnotations, blockId, callback) {
    if (!yAnnotations || !blockId || typeof callback !== 'function') return () => {};

    const handler = (events, transaction) => {
        const relevant = events.some((e) =>
            (e.path.length > 0 && e.path[0] === blockId) ||
            (e.path.length === 0 && e.keysChanged?.has(blockId))
        );
        if (relevant) callback(events, transaction);
    };

    yAnnotations.observeDeep(handler);
    return () => yAnnotations.unobserveDeep(handler);
}
